import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import SpotifyEmbed from '../SpotifyEmbed';

const API = process.env.REACT_APP_API_URL || '';

function LanguageConflicts() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState(null);
  const [listening, setListening] = useState({}); // `${recordingId}-${isrc}` -> true
  const [dismissed, setDismissed] = useState({}); // recordingId -> true, "leave as one recording"

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/library/language-conflicts`, { params: { limit: 300 } })
      .then(res => {
        setRows(res.data.rows);
        setTotal(res.data.total);
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSplit = async (recording, isrc) => {
    const key = recording.id + '-' + isrc;
    setBusyKey(key);
    try {
      await axios.post(`${API}/api/library/recordings/${recording.id}/split`, { isrc });
      setRows(rs => rs.filter(r => r.id !== recording.id));
      setTotal(t => t - 1);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyKey(null);
    }
  };

  const handleLeaveAsOne = (recording) => {
    setDismissed(d => ({ ...d, [recording.id]: true }));
  };

  const visibleRows = rows.filter(r => !dismissed[r.id]);

  return (
    <div>
      <Header>
        <div>
          <Title>Language Conflicts</Title>
          <Subtitle>
            {loading ? 'Loading...' : `${visibleRows.length.toLocaleString()} recording${visibleRows.length === 1 ? '' : 's'} to review`}
          </Subtitle>
        </div>
      </Header>

      <InfoBox>
        These recordings carry more than one ISRC, and the releases each ISRC was used on point to different
        languages (inferred from the language suffix on the product code, e.g. PF031ES → Spanish). That can be
        genuinely correct — the same master reused across markets, or an instrumental with nothing to translate —
        or it can mean two different-language recordings were merged by mistake before language checking existed.
        Listen to each ISRC to judge; if it's really the same recording, leave it as one. If not, split the
        wrong ISRC back out into its own recording — its release history moves with it, nothing is lost.
      </InfoBox>

      {error && <ErrorBox>{error}</ErrorBox>}

      {!loading && visibleRows.length === 0 && (
        <EmptyState>No language conflicts to review.</EmptyState>
      )}

      <Cards>
        {visibleRows.map(r => (
          <Card key={r.id}>
            <CardTitle>{r.title}{r.version ? <VersionTag> · {r.version}</VersionTag> : null}</CardTitle>
            <CardArtist>{r.primary_artist}</CardArtist>

            <IsrcList>
              {r.isrcs.map(i => {
                const key = r.id + '-' + i.isrc;
                return (
                  <IsrcRow key={i.isrc}>
                    <IsrcTop>
                      <IsrcInfo>
                        <Mono>{i.isrc}</Mono>
                        {i.is_canonical && <CanonicalTag>canonical</CanonicalTag>}
                        {i.languages.length > 0 && <LangTag>{i.languages.join('/')}</LangTag>}
                        {i.source_title && i.source_title !== r.title && (
                          <SourceTitleNote>filed as "{i.source_title}"</SourceTitleNote>
                        )}
                      </IsrcInfo>
                      <IsrcActions>
                        <ListenBtn onClick={() => setListening(l => ({ ...l, [key]: !l[key] }))}>
                          {listening[key] ? 'Hide' : 'Listen'}
                        </ListenBtn>
                        <SplitBtn
                          disabled={busyKey === key}
                          onClick={() => handleSplit(r, i.isrc)}
                        >
                          Split off
                        </SplitBtn>
                      </IsrcActions>
                    </IsrcTop>
                    {listening[key] && (
                      <EmbedSlot><SpotifyEmbed isrc={i.isrc} compact /></EmbedSlot>
                    )}
                  </IsrcRow>
                );
              })}
            </IsrcList>

            <LeaveBtn onClick={() => handleLeaveAsOne(r)}>
              Same recording, reused across languages — leave as one
            </LeaveBtn>
          </Card>
        ))}
      </Cards>
    </div>
  );
}

const Header = styled.div`
  margin-bottom: 14px;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.3px;
  color: var(--text);
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-top: 2px;
`;

const InfoBox = styled.div`
  background: var(--blue-dim);
  color: var(--blue-dark);
  font-size: 12px;
  padding: 12px 16px;
  border-radius: var(--radius);
  margin-bottom: 20px;
  line-height: 1.5;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 14px;
`;

const Card = styled.div`
  background: var(--bg-2);
  border-radius: var(--radius);
  padding: 16px;
  box-shadow: var(--shadow-sm);
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--text);
`;

const VersionTag = styled.span`
  font-weight: 600;
  color: var(--text-2);
`;

const CardArtist = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-bottom: 12px;
`;

const IsrcList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`;

const IsrcRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px;
  background: var(--bg-3);
  border-radius: 8px;
`;

const IsrcTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const IsrcInfo = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
`;

const IsrcActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text);
  font-weight: 600;
`;

const CanonicalTag = styled.span`
  font-size: 9px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: var(--accent-text);
  background: var(--accent-dim);
  padding: 2px 6px;
  border-radius: 8px;
`;

const LangTag = styled.span`
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 600;
  color: var(--text-2);
  background: var(--bg-4);
  padding: 2px 7px;
  border-radius: 8px;
`;

const SourceTitleNote = styled.span`
  font-size: 10.5px;
  color: var(--text-3);
  font-style: italic;
`;

const ListenBtn = styled.button`
  background: none;
  border: 1px solid var(--border);
  color: var(--text-2);
  font-size: 10.5px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: var(--radius);
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { border-color: var(--accent-border); color: var(--accent-text); }
`;

const SplitBtn = styled.button`
  background: var(--gradient-ocean);
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: var(--radius);
  white-space: nowrap;
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const EmbedSlot = styled.div`
  padding-top: 2px;
`;

const LeaveBtn = styled.button`
  width: 100%;
  background: none;
  border: 1.5px solid var(--border);
  color: var(--text-2);
  font-size: 11px;
  font-weight: 700;
  padding: 7px;
  border-radius: var(--radius);
  transition: all 0.15s;
  &:hover {
    border-color: var(--green);
    color: var(--green);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-3);
  font-size: 13px;
`;

const ErrorBox = styled.div`
  background: var(--red-dim);
  color: var(--red);
  padding: 10px 14px;
  border-radius: var(--radius);
  margin-bottom: 14px;
  font-size: 13px;
`;

export default LanguageConflicts;
